import { Offer, PageConfig } from '@tixa/schema'
import { AnalyticsEventsHandler } from './types'

import { initFacebookPixel } from './initFacebookPixel.js'

const convertOffers = (offers: Offer[]): fb.Pixel.ViewContentParameters => {
  return {
    content_ids: offers.map((offer) => offer.sku),
    content_type: 'product',
    contents: offers.map((item) => {
      return {
        id: item.sku,
        quantity: item.eligibleQuantity.value,
      }
    }),
    currency: offers.length > 0 ? offers[0].priceCurrency : '',
    value: offers
      .map((offer) => offer.price)
      .reduce((prev, curr) => prev + curr, 0),
  }
}

const createFacebookPixelHandler = (): AnalyticsEventsHandler => {
  let isActive = false

  const send = (event: string, parameters?: fb.Pixel.ViewContentParameters) => {
    if (isActive) {
      if (parameters) {
        fbq('track', event, parameters)
      } else {
        fbq('track', event)
      }
    }
  }

  return {
    init: async (config: PageConfig) => {
      if (config.fbPixel) {
        initFacebookPixel(config.fbPixel)
        try {
          if (!config.fbEventRelayEndpoint) {
            isActive = true
            // Send all server-sent events to Facebook
            if (undefined !== config.fbTrack) {
              config.fbTrack
                .filter((item) => item.eventName !== undefined)
                .forEach((item) => {
                  send(item.eventName, item.customData)
                })
            }
          }
        } catch {
          // Couldn't load Facebook
        }
      }
    },
    searched: () => send('Search'),
    viewed: (offers: Offer[]) => send('ViewContent', convertOffers(offers)),
    addedToCart: (params: {
      name: string
      offerId: string
      ticketTypeName: string
      ticketTypeId: string
      offerPrice: number
      currency: string
      quantity: number
      coupon?: string
    }) => {
      send('AddToCart', {
        content_ids: [params.ticketTypeId.toString()],
        content_name: params.offerId,
        content_category: params.ticketTypeName,
        content_type: 'product',
        currency: params.currency,
        value: params.offerPrice,
      })
    },
    checkoutStarted: (cartId: string, offers: Offer[]) =>
      send('InitiateCheckout', convertOffers(offers)),
    paymentInfoAdded: (offers: Offer[]) =>
      send('AddPaymentInfo', convertOffers(offers)),
    purchased: (id: string, offers: Offer[]) => {
      const fbObj = convertOffers(offers) as fb.Pixel.PurchaseParameters
      fbObj.order_id = id
      send('Purchase', fbObj)
    },
    registered: (value: number, currency: string) =>
      send('CompleteRegistration', { value, currency }),
  }
}

export { createFacebookPixelHandler }
